import { ShoppingOutlined } from "@ant-design/icons";
import {
  Badge,
  Button,
  Card,
  Col,
  Input,
  Row,
  Select,
  Statistic,
  Table,
  TableColumnType,
  Tag,
  Typography,
} from "antd";
import { debounce } from "lodash";
import React, { useCallback } from "react";
import { Link } from "react-router-dom";
import {
  PriorityMark,
  PurchaseRequestStatus,
  PurchaseTaskType,
} from "../../enum/common";
import { IPurchaseFilter, IPurchaseRequest } from "../../models/Purchase";
import {
  useGetPurchaseOrderOverview,
  useGetPurchaseRequest,
} from "../../query/PurchaseQuery";
const { Text } = Typography;
const PurchaseRequest = () => {
  const [filter, setFilter] = React.useState<IPurchaseFilter>({
    page: 1,
    limit: 10,
  });
  const { data: overview, isLoading: isOverviewLoading } =
    useGetPurchaseOrderOverview();
  const { data, isLoading } = useGetPurchaseRequest(filter);
  // eslint-disable-next-line
  const handleSearch = useCallback(
    debounce((value: string) => {
      setFilter((prev) => ({
        ...prev,
        pONumber: value || undefined,
        page: 1,
      }));
    }, 500),
    [],
  );
  const colProps = {
    xs: 24,
    md: 8,
  };
  interface PriorityBadgeProps {
    priority: PriorityMark;
    source: string;
  }

  const PriorityBadge: React.FC<PriorityBadgeProps> = ({
    priority,
    source,
  }) => {
    switch (priority) {
      case PriorityMark.HIGH:
        return (
          <Badge color="red" count="Rất gấp">
            <Text>{source}</Text>
          </Badge>
        );

      case PriorityMark.MEDIUM:
        return (
          <Badge color="orange" count="Gấp">
            <Text>{source}</Text>
          </Badge>
        );

      case PriorityMark.LOW:
        return (
          <Badge color="green" count="Thường">
            <Text>{source}</Text>
          </Badge>
        );

      default:
        return (
          <Badge color="default" count="Không xác định">
            <Text>{source}</Text>
          </Badge>
        );
    }
  };
  const column: TableColumnType<IPurchaseRequest>[] = [
    {
      title: "Mã yêu cầu",
      dataIndex: "requestNumber",
      key: "requestNumber",
      align: "center",
      filterDropdown: () => (
        <div className="p-2">
          <Input
            placeholder="Tìm kiếm"
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
      ),
    },
    {
      title: "Nguồn yêu cầu",
      dataIndex: "source",
      key: "source",
      render: (value: string, record: IPurchaseRequest) => {
        if (value === PurchaseTaskType.RD_REQUEST)
          return (
            <PriorityBadge
              priority={record.priority}
              source="Yêu cầu mua từ R&D"
            />
          );
        if (value === PurchaseTaskType.SALE_ORDER)
          return (
            <PriorityBadge
              priority={record.priority}
              source="Yêu cầu mua từ sale"
            />
          );

        if (value === PurchaseTaskType.DIRECTOR)
          return (
            <PriorityBadge
              priority={record.priority}
              source="Yêu cầu mua từ Giám đốc"
            />
          );
        if (value === PurchaseTaskType.PRD_REQUEST)
          return (
            <PriorityBadge
              priority={record.priority}
              source="Yêu cầu mua từ Sản xuất"
            />
          );
      },
      align: "center",
      filterDropdown: () => (
        <div className="p-2">
          <Select
            placeholder="Tìm kiếm"
            allowClear
            className="w-48"
            onChange={(value: PurchaseTaskType) => {
              setFilter((prev) => ({
                ...prev,
                source: value || undefined,
                page: 1,
              }));
            }}
          >
            <Select.Option value={PurchaseTaskType.RD_REQUEST}>
              Yêu cầu từ R&D
            </Select.Option>
            <Select.Option value={PurchaseTaskType.SALE_ORDER}>
              Yêu cầu mua từ sale
            </Select.Option>
            <Select.Option value={PurchaseTaskType.DIRECTOR}>
              Yêu cầu từ giám đốc
            </Select.Option>
            <Select.Option value={PurchaseTaskType.PRD_REQUEST}>
              Yêu cầu từ sản xuất
            </Select.Option>
          </Select>
        </div>
      ),
    },
    {
      title: "Yêu cầu bởi",
      dataIndex: "requestBy",
      key: "requestBy",
      align: "center",
    },
    {
      title: "Tên dự án",
      dataIndex: "projectName",
      key: "projectName",
      align: "center",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value: string) => {
        if (value === "DRAFT") return <Tag color="orange">Chờ mua</Tag>;
        if (value === "SUBMITTED") return <Tag color="blue">Đã gửi</Tag>;
        if (value === "APPROVED") return <Tag color="green">Đã duyệt</Tag>;
        if (value === "REJECTED") return <Tag color="red">Từ chối</Tag>;
        if (value === "CLOSED")
          return <Tag color="purple">Đã đóng/Hoàn thành</Tag>;
      },
      align: "center",
      filterDropdown: () => (
        <div className="p-2">
          <Select
            placeholder="Tìm kiếm"
            allowClear
            className="w-48"
            onChange={(value: PurchaseRequestStatus) => {
              setFilter((prev) => ({
                ...prev,
                status: value || undefined,
                page: 1,
              }));
            }}
          >
            <Select.Option value={"DRAFT"}>Chờ duyệt</Select.Option>
            <Select.Option value={"SUBMITTED"}>Đã gửi</Select.Option>
            <Select.Option value={"APPROVED"}>Đã duyệt</Select.Option>
            <Select.Option value={"REJECTED"}>Từ chối</Select.Option>
            <Select.Option value={"CLOSED"}>Đã đóng</Select.Option>
          </Select>
        </div>
      ),
    },

    {
      title: "Tổng tiền ước tính",
      dataIndex: "estimatedAmount",
      key: "estimatedAmount",
      render: (value: number) => {
        return new Intl.NumberFormat("vi-VN", {
          style: "currency",
          currency: "VND",
        }).format(value);
      },
      align: "center",
    },
    {
      title: "Ghi chú",
      dataIndex: "note",
      key: "note",
      align: "center",
    },

    {
      title: "Hạn mua",
      dataIndex: "deadline",
      key: "deadline",
      render: (value: Date) => {
        return new Date(value).toLocaleString();
      },
      align: "center",
    },
    {
      title: "Yêu cầu lúc",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (value: Date) => {
        return new Date(value).toLocaleString();
      },
      align: "center",
    },
    {
      title: "Hành động",
      dataIndex: "action",
      key: "action",
      align: "center",
      render: (_any, record: IPurchaseRequest) => {
        return (
          <div>
            <Link to={`${record.requestNumber}`}>
              <Button type="primary">Chi tiết</Button>
            </Link>
          </div>
        );
      },
    },
  ];
  return (
    <div className="p-4">
      <Row gutter={[16, 16]} className="mb-4">
        <Col {...colProps}>
          <Card className="w-full shadow-md bg-gradient-to-r from-pink-300 to-pink-400 cursor-pointer">
            <Statistic
              prefix={<ShoppingOutlined />}
              title="Tổng số đơn hàng"
              value={overview?.total}
              loading={isOverviewLoading}
              valueStyle={{ color: "blue" }}
            />
          </Card>
        </Col>
        <Col {...colProps}>
          <Card className="w-full">
            <Statistic
              title="Số đơn chờ mua"
              valueStyle={{ color: "orange" }}
              value={overview?.pending}
              loading={isOverviewLoading}
            />
          </Card>
        </Col>
        <Col {...colProps}>
          <Card className="w-full">
            <Statistic
              title="Số đơn chưa giao phó"
              valueStyle={{ color: "red" }}
              value={overview?.unassigned}
              loading={isOverviewLoading}
            />
          </Card>
        </Col>
      </Row>
      <Table
        dataSource={data?.data}
        columns={column}
        loading={isLoading}
        scroll={{ x: 1200 }}
      />
    </div>
  );
};

export default PurchaseRequest;
